import React from 'react';
import { useIntl } from 'react-intl';
import { IconClose } from '../../assets/svg/icons';
import {
  StyledCartItem,
  StyledCartItemImage,
  StyledCartItemInfo,
  StyledCartItemMeta,
  StyledCartItemName,
  StyledCartItemPrice,
  StyledCartTotalLabel,
  StyledCartTotalRow,
  StyledCartTotalValue,
  StyledCheckoutButton,
  StyledCloseButton,
  StyledDrawer,
  StyledDrawerBody,
  StyledDrawerFooter,
  StyledDrawerHeader,
  StyledDrawerOverlay,
  StyledEmptyCart,
  StyledModalTitle,
  StyledRemoveButton,
} from './styles';
import { CartItem } from './types';

interface CartDrawerProps {
  items: CartItem[];
  onClose: () => void;
  onRemove: (productId: number) => void;
  onCheckout: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({
  items,
  onClose,
  onRemove,
  onCheckout,
}) => {
  const intl = useIntl();
  const total = items.reduce((sum, item) => sum + item.qty * item.product.priceQI, 0);
  const totalUnits = items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <>
      <StyledDrawerOverlay onClick={onClose} />
      <StyledDrawer>
        <StyledDrawerHeader>
          <StyledModalTitle>
            {intl.formatMessage({ id: 'marketplace.cart.title', defaultMessage: 'Your cart{count, plural, =0 {} other { ({count})}}' }, { count: items.length })}
          </StyledModalTitle>
          <StyledCloseButton onClick={onClose} type='button' aria-label='Close'>
            <IconClose />
          </StyledCloseButton>
        </StyledDrawerHeader>

        <StyledDrawerBody>
          {items.length === 0 ? (
            <StyledEmptyCart>
              <div>🛒</div>
              <div>{intl.formatMessage({ id: 'marketplace.cart.empty', defaultMessage: 'Your cart is empty' })}</div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                {intl.formatMessage({ id: 'marketplace.cart.emptyHint', defaultMessage: 'Add products from the catalog to get started.' })}
              </div>
            </StyledEmptyCart>
          ) : (
            items.map((item) => (
              <StyledCartItem key={item.product.id}>
                <StyledCartItemImage bg={item.product.bg}>
                  {item.product.emoji}
                </StyledCartItemImage>
                <StyledCartItemInfo>
                  <StyledCartItemName>{item.product.name}</StyledCartItemName>
                  <StyledCartItemMeta>
                    {item.qty} × {item.product.priceQI.toLocaleString()} QI
                  </StyledCartItemMeta>
                  <StyledRemoveButton
                    type='button'
                    onClick={() => onRemove(item.product.id)}
                  >
                    {intl.formatMessage({ id: 'marketplace.cart.remove', defaultMessage: 'Remove' })}
                  </StyledRemoveButton>
                </StyledCartItemInfo>
                <StyledCartItemPrice>
                  {(item.qty * item.product.priceQI).toLocaleString()} QI
                </StyledCartItemPrice>
              </StyledCartItem>
            ))
          )}
        </StyledDrawerBody>

        <StyledDrawerFooter>
          <StyledCartTotalRow>
            <div>
              <StyledCartTotalLabel>
                {intl.formatMessage({ id: 'marketplace.cart.subtotal', defaultMessage: 'Subtotal{count, plural, =0 {} other { · {count} units}}' }, { count: totalUnits })}
              </StyledCartTotalLabel>
              <StyledCartTotalValue>{total.toLocaleString()} QI</StyledCartTotalValue>
            </div>
          </StyledCartTotalRow>
          <StyledCheckoutButton
            type='button'
            onClick={onCheckout}
            disabled={items.length === 0}
          >
            {intl.formatMessage({ id: 'marketplace.cart.checkout', defaultMessage: 'Proceed to Checkout' })}
          </StyledCheckoutButton>
        </StyledDrawerFooter>
      </StyledDrawer>
    </>
  );
};

export default CartDrawer;
